/**
 * Sidebar State Hook
 * Manages sidebar collapse state with keyboard shortcut support
 * Includes "Report Mode" for focused report writing with embedded viewer
 */

import { useState, useEffect, useCallback, createContext, useContext } from 'react';

const SIDEBAR_STORAGE_KEY = 'pacs-sidebar-collapsed';

// Context for Report Mode (accessible across components)
const ReportModeContext = createContext(null);

export const ReportModeProvider = ({ children }) => {
  const [reportMode, setReportMode] = useState(false);
  const [reportModeData, setReportModeData] = useState(null);
  const [onSubmitComplete, setOnSubmitComplete] = useState(null);

  const enterReportMode = useCallback((data, submitCallback = null) => {
    setReportModeData(data);
    setOnSubmitComplete(() => submitCallback);
    setReportMode(true);
  }, []);

  const exitReportMode = useCallback(() => {
    setReportMode(false);
    setReportModeData(null);
    setOnSubmitComplete(null);
  }, []);

  // Called when report is submitted successfully in report mode
  const handleReportModeSubmit = useCallback(() => {
    if (onSubmitComplete) {
      onSubmitComplete();
    }
    exitReportMode();
  }, [onSubmitComplete, exitReportMode]);

  return (
    <ReportModeContext.Provider value={{
      reportMode,
      reportModeData,
      enterReportMode,
      exitReportMode,
      handleReportModeSubmit
    }}>
      {children}
    </ReportModeContext.Provider>
  );
};

export const useReportMode = () => {
  const context = useContext(ReportModeContext);
  if (!context) {
    // Return default values if not wrapped in provider
    return {
      reportMode: false,
      reportModeData: null,
      enterReportMode: () => {},
      exitReportMode: () => {},
      handleReportModeSubmit: () => {},
    };
  }
  return context;
};

export const useSidebar = () => {
  // Initialize from localStorage
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(SIDEBAR_STORAGE_KEY);
      return stored === 'true';
    }
    return false;
  });

  // Mobile drawer state
  const [mobileOpen, setMobileOpen] = useState(false);

  // Get report mode from context
  const { reportMode } = useReportMode();

  // Toggle collapse state
  const toggleCollapse = useCallback(() => {
    setIsCollapsed(prev => {
      const newValue = !prev;
      localStorage.setItem(SIDEBAR_STORAGE_KEY, String(newValue));
      return newValue;
    });
  }, []);

  // Toggle mobile drawer
  const toggleMobile = useCallback(() => {
    setMobileOpen(prev => !prev);
  }, []);

  // Close mobile drawer
  const closeMobile = useCallback(() => {
    setMobileOpen(false);
  }, []);

  // Keyboard shortcut: Cmd/Ctrl + B to toggle sidebar
  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'b') {
        event.preventDefault();
        toggleCollapse();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleCollapse]);

  // In report mode, always collapse sidebar
  const effectiveCollapsed = reportMode ? true : isCollapsed;

  return {
    isCollapsed: effectiveCollapsed,
    mobileOpen,
    toggleCollapse,
    toggleMobile,
    closeMobile,
    setIsCollapsed,
    reportMode,
  };
};

export default useSidebar;
